import {
  Build as BuildIcon,
  Inventory as InventoryIcon,
  People as PeopleIcon,
  TrendingUp as TrendingUpIcon,
} from '@mui/icons-material';
import { Box, Card, CardContent, Grid, Skeleton, Typography } from '@mui/material';
import { useEffect, useState } from 'react';
import { supabase } from '../lib/supabase';

interface DashboardStats {
  customers: number;
  assets: number;
  activeAssets: number;
  recentEvents: number;
}

export function Dashboard() {
  const [stats, setStats] = useState<DashboardStats | null>(null);
  const [loading, setLoading] = useState(true);

  async function fetchStats() {
    try {
      const [customersResult, assetsResult, activeAssetsResult, eventsResult] = await Promise.all([
        supabase.from('customers').select('*', { count: 'exact', head: true }),
        supabase.from('assets').select('*', { count: 'exact', head: true }),
        supabase.from('assets').select('*', { count: 'exact', head: true }).eq('status', 'active'),
        supabase
          .from('asset_histories')
          .select('*', { count: 'exact', head: true })
          .gte('event_at', new Date(Date.now() - 30 * 24 * 60 * 60 * 1000).toISOString()),
      ]);

      setStats({
        customers: customersResult.count || 0,
        assets: assetsResult.count || 0,
        activeAssets: activeAssetsResult.count || 0,
        recentEvents: eventsResult.count || 0,
      });
    } catch (error) {
      console.error('Error fetching dashboard stats:', error);
    } finally {
      setLoading(false);
    }
  }

  useEffect(() => {
    fetchStats();
  }, []);

  const statCards = [
    {
      title: 'Total Customers',
      value: stats?.customers ?? 0,
      icon: <PeopleIcon />,
      color: '#1976d2',
    },
    {
      title: 'Total Assets',
      value: stats?.assets ?? 0,
      icon: <InventoryIcon />,
      color: '#2e7d32',
    },
    {
      title: 'Active Assets',
      value: stats?.activeAssets ?? 0,
      icon: <TrendingUpIcon />,
      color: '#ed6c02',
    },
    {
      title: 'Events (30 days)',
      value: stats?.recentEvents ?? 0,
      icon: <BuildIcon />,
      color: '#9c27b0',
    },
  ];

  return (
    <Box>
      <Typography variant="h4" component="h1" fontWeight={600} gutterBottom>
        Dashboard
      </Typography>
      <Typography variant="body1" color="text.secondary" sx={{ mb: 4 }}>
        Welcome back! Here&apos;s an overview of your CRM.
      </Typography>

      <Grid container spacing={3}>
        {statCards.map(card => (
          <Grid size={{ xs: 12, sm: 6, md: 3 }} key={card.title}>
            <Card>
              <CardContent>
                <Box sx={{ display: 'flex', alignItems: 'center', mb: 2 }}>
                  <Box
                    sx={{
                      p: 1.5,
                      borderRadius: 2,
                      bgcolor: `${card.color}15`,
                      color: card.color,
                      mr: 2,
                    }}
                  >
                    {card.icon}
                  </Box>
                  <Typography variant="body2" color="text.secondary">
                    {card.title}
                  </Typography>
                </Box>
                {loading ? (
                  <Skeleton variant="text" width={80} height={40} />
                ) : (
                  <Typography variant="h4" fontWeight={700}>
                    {card.value.toLocaleString()}
                  </Typography>
                )}
              </CardContent>
            </Card>
          </Grid>
        ))}
      </Grid>

      <Grid container spacing={3} sx={{ mt: 2 }}>
        <Grid size={{ xs: 12, md: 8 }}>
          <Card>
            <CardContent>
              <Typography variant="h6" gutterBottom>
                Recent Activity
              </Typography>
              <Typography variant="body2" color="text.secondary">
                No recent activity to display.
              </Typography>
            </CardContent>
          </Card>
        </Grid>
        <Grid size={{ xs: 12, md: 4 }}>
          <Card>
            <CardContent>
              <Typography variant="h6" gutterBottom>
                Quick Actions
              </Typography>
              <Typography variant="body2" color="text.secondary">
                Use the sidebar to navigate to different sections.
              </Typography>
            </CardContent>
          </Card>
        </Grid>
      </Grid>
    </Box>
  );
}
